import { useCallback, useEffect, useState, type CSSProperties, type ReactElement } from "react";
import { formatBytes, isAudio, isImage, isVideo } from "@shared/protocol";
import type { Attachment } from "@shared/types";
import { CloseIcon } from "@/components/icons";
import { C, FONT_MONO } from "@/theme";

/** Largura maxima do anexo dentro da conversa, igual ao cartao do desenho. */
const MAX_WIDTH = 420;

const CARD: CSSProperties = {
  display: "flex",
  alignItems: "center",
  gap: 10,
  width: "100%",
  maxWidth: MAX_WIDTH,
  padding: "9px 12px",
  border: `1px solid ${C.lineStrong}`,
  borderRadius: 10,
  background: C.raised,
  boxSizing: "border-box",
};

const NAME: CSSProperties = {
  display: "block",
  fontFamily: FONT_MONO,
  fontSize: 12,
  color: "var(--ac, #1F4FD8)",
  whiteSpace: "nowrap",
  overflow: "hidden",
  textOverflow: "ellipsis",
};

const META: CSSProperties = { display: "block", fontSize: 11, color: C.textFaint, marginTop: 1 };

function FileGlyph({ color = "currentColor" }: { color?: string }): ReactElement {
  return (
    <svg
      width={13}
      height={13}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M13.5 3H7a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h10a2 2 0 0 0 2-2V8.5L13.5 3z" />
      <path d="M13.5 3v5.5H19" />
    </svg>
  );
}

function DownloadGlyph({ color = "currentColor" }: { color?: string }): ReactElement {
  return (
    <svg
      width={14}
      height={14}
      viewBox="0 0 24 24"
      fill="none"
      stroke={color}
      strokeWidth={1.7}
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d="M12 4v11" />
      <path d="M7.5 10.5 12 15l4.5-4.5" />
      <path d="M4.5 19.5h15" />
    </svg>
  );
}

function SoundGlyph({ color = "currentColor" }: { color?: string }): ReactElement {
  return (
    <svg width={13} height={13} viewBox="0 0 24 24" fill={color}>
      <path d="M4 9.5v5h3.5L13 19V5L7.5 9.5H4z" />
    </svg>
  );
}

/** Camada de tela cheia da imagem: fecha no fundo, no botao e no Escape. */
function Lightbox({
  attachment,
  onClose,
}: {
  attachment: Attachment;
  onClose: () => void;
}): ReactElement {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return (
    <div
      onClick={onClose}
      role="presentation"
      style={{
        position: "fixed",
        inset: 0,
        zIndex: 80,
        display: "grid",
        gridTemplateRows: "1fr auto",
        placeItems: "center",
        gap: 12,
        padding: 32,
        background: "rgba(0,0,0,.86)",
        cursor: "zoom-out",
        animation: "fadein 140ms ease",
      }}
    >
      <img
        src={attachment.url}
        alt={attachment.name}
        onClick={event => event.stopPropagation()}
        style={{
          maxWidth: "92vw",
          maxHeight: "82vh",
          objectFit: "contain",
          borderRadius: 10,
          cursor: "default",
        }}
      />
      <span
        style={{
          fontFamily: FONT_MONO,
          fontSize: 11,
          letterSpacing: "0.04em",
          color: C.textMuted,
        }}
      >
        {attachment.name} · {formatBytes(attachment.size)}
      </span>
      <button
        type="button"
        className="hov-bg-26-f2"
        onClick={onClose}
        title="Fechar"
        aria-label="Fechar"
        style={{
          position: "fixed",
          top: 18,
          right: 18,
          width: 34,
          height: 34,
          display: "grid",
          placeItems: "center",
          border: `1px solid ${C.lineStrong}`,
          borderRadius: 9,
          cursor: "pointer",
          background: C.raised,
          color: C.textSoft,
        }}
      >
        <CloseIcon size={14} color="currentColor" />
      </button>
    </div>
  );
}

export type AttachmentViewProps = {
  attachment: Attachment;
};

/** Anexo real: imagem, video, audio ou cartao de arquivo com botao de baixar. */
export function AttachmentView({ attachment }: AttachmentViewProps): ReactElement {
  const [zoom, setZoom] = useState(false);
  const closeZoom = useCallback(() => setZoom(false), []);

  if (isImage(attachment.mime)) {
    const ratio =
      attachment.width && attachment.height
        ? `${attachment.width} / ${attachment.height}`
        : undefined;
    return (
      <>
        <img
          src={attachment.url}
          alt={attachment.name}
          title={`${attachment.name} · ${formatBytes(attachment.size)}`}
          onClick={() => setZoom(true)}
          style={{
            display: "block",
            maxWidth: MAX_WIDTH,
            maxHeight: 420,
            width: "auto",
            height: "auto",
            aspectRatio: ratio,
            objectFit: "contain",
            border: `1px solid ${C.lineStrong}`,
            borderRadius: 10,
            background: C.field,
            cursor: "zoom-in",
          }}
        />
        {zoom && <Lightbox attachment={attachment} onClose={closeZoom} />}
      </>
    );
  }

  if (isVideo(attachment.mime)) {
    return (
      <video
        src={attachment.url}
        controls
        preload="metadata"
        style={{
          display: "block",
          width: MAX_WIDTH,
          maxWidth: "100%",
          maxHeight: 420,
          border: `1px solid ${C.lineStrong}`,
          borderRadius: 10,
          background: "#000",
        }}
      />
    );
  }

  if (isAudio(attachment.mime)) {
    return (
      <div
        style={{
          width: MAX_WIDTH,
          maxWidth: "100%",
          padding: "9px 12px",
          border: `1px solid ${C.lineStrong}`,
          borderRadius: 10,
          background: C.raised,
          boxSizing: "border-box",
        }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <span
            style={{
              width: 26,
              height: 26,
              flex: "0 0 auto",
              display: "grid",
              placeItems: "center",
              borderRadius: 999,
              background: "var(--acs, rgba(46,107,255,.14))",
              color: "var(--ac, #1F4FD8)",
            }}
          >
            <SoundGlyph />
          </span>
          <span style={{ flex: 1, minWidth: 0 }}>
            <span style={NAME}>{attachment.name}</span>
            <span style={META}>{formatBytes(attachment.size)}</span>
          </span>
        </div>
        <audio
          src={attachment.url}
          controls
          preload="metadata"
          style={{ width: "100%", height: 34, marginTop: 8 }}
        />
      </div>
    );
  }

  return (
    <div style={CARD}>
      <span
        style={{
          width: 26,
          height: 26,
          flex: "0 0 auto",
          display: "grid",
          placeItems: "center",
          borderRadius: 999,
          background: "var(--acs, rgba(46,107,255,.14))",
          color: "var(--ac, #1F4FD8)",
        }}
      >
        <FileGlyph />
      </span>
      <span style={{ flex: 1, minWidth: 0 }}>
        <span style={NAME}>{attachment.name}</span>
        <span style={META}>{formatBytes(attachment.size)}</span>
      </span>
      <a
        className="hov-bg-24-fg"
        href={attachment.url}
        download={attachment.name}
        title="Baixar arquivo"
        style={{
          width: 30,
          height: 30,
          flex: "0 0 auto",
          display: "grid",
          placeItems: "center",
          borderRadius: 8,
          background: "transparent",
          color: C.textMuted,
          textDecoration: "none",
        }}
      >
        <DownloadGlyph />
      </a>
    </div>
  );
}
